import BroadcastsAPI from '../../api/broadcasts';

export const state = {
  records: [],
  currentCampaign: null,
  recipients: [],
  lastError: null,
  recipientsMeta: {
    total: 0,
    page: 1,
    perPage: 50,
    totalPages: 1,
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingRecipients: false,
  },
};

export const getters = {
  getCampaigns($state) {
    return $state.records;
  },
  getCurrentCampaign($state) {
    return $state.currentCampaign;
  },
  getRecipients($state) {
    return $state.recipients;
  },
  getRecipientsMeta($state) {
    return $state.recipientsMeta;
  },
  getLastError($state) {
    return $state.lastError;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

const normalizeApiError = (error, fallbackMessage) => {
  return {
    message:
      error?.response?.data?.error ||
      fallbackMessage ||
      'Unable to complete the request.',
    status: error?.response?.status || null,
    requestId: error?.response?.data?.request_id || null,
  };
};

export const actions = {
  async fetch({ commit }) {
    commit('setUIFlags', { isFetching: true });
    commit('setLastError', null);
    try {
      const response = await BroadcastsAPI.get();
      commit('setCampaigns', response.data);
    } catch (error) {
      commit('setCampaigns', []);
      commit(
        'setLastError',
        normalizeApiError(error, 'Falha ao carregar campanhas.')
      );
    } finally {
      commit('setUIFlags', { isFetching: false });
    }
  },

  async fetchOne({ commit }, id) {
    commit('setLastError', null);
    try {
      const response = await BroadcastsAPI.show(id);
      commit('setCurrentCampaign', response.data);
      return response.data;
    } catch (error) {
      commit('setCurrentCampaign', null);
      commit(
        'setLastError',
        normalizeApiError(error, 'Falha ao carregar campanha.')
      );
      throw error;
    }
  },

  async create({ commit }, payload) {
    commit('setUIFlags', { isCreating: true });
    try {
      const response = await BroadcastsAPI.create(payload);
      commit('addCampaign', response.data);
      return response.data;
    } finally {
      commit('setUIFlags', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...payload }) {
    commit('setUIFlags', { isUpdating: true });
    try {
      const response = await BroadcastsAPI.update(id, payload);
      commit('replaceCampaign', response.data);
      return response.data;
    } finally {
      commit('setUIFlags', { isUpdating: false });
    }
  },

  async delete({ commit }, id) {
    commit('setUIFlags', { isDeleting: true });
    try {
      await BroadcastsAPI.delete(id);
      commit('removeCampaign', id);
    } finally {
      commit('setUIFlags', { isDeleting: false });
    }
  },

  async start({ commit }, id) {
    const response = await BroadcastsAPI.startCampaign(id);
    commit('replaceCampaign', response.data);
    return response.data;
  },

  async pause({ commit }, id) {
    const response = await BroadcastsAPI.pauseCampaign(id);
    commit('replaceCampaign', response.data);
    return response.data;
  },

  async resume({ commit }, id) {
    const response = await BroadcastsAPI.resumeCampaign(id);
    commit('replaceCampaign', response.data);
    return response.data;
  },

  async fetchRecipients({ commit }, { campaignId, page = 1 }) {
    commit('setUIFlags', { isFetchingRecipients: true });
    try {
      const response = await BroadcastsAPI.getRecipients(campaignId, page);
      commit('setRecipients', response.data.recipients || []);
      commit('setRecipientsMeta', response.data.meta || {});
      return response.data;
    } catch (error) {
      commit('setRecipients', []);
      commit('setRecipientsMeta', {});
      throw error;
    } finally {
      commit('setUIFlags', { isFetchingRecipients: false });
    }
  },

  async createRecipient({ dispatch }, { campaignId, payload }) {
    await BroadcastsAPI.createRecipient(campaignId, payload);
    return dispatch('fetchRecipients', { campaignId });
  },

  async deleteRecipient({ dispatch }, { campaignId, recipientId }) {
    await BroadcastsAPI.deleteRecipient(campaignId, recipientId);
    return dispatch('fetchRecipients', { campaignId });
  },

  async importRecipients({ dispatch }, { campaignId, file }) {
    const response = await BroadcastsAPI.importRecipients(campaignId, file);
    await dispatch('fetchRecipients', { campaignId });
    return response.data;
  },

  async addAllContacts({ dispatch }, { campaignId }) {
    const response = await BroadcastsAPI.addAllContacts(campaignId);
    await dispatch('fetchRecipients', { campaignId });
    return response.data;
  },
};

export const mutations = {
  setCampaigns($state, campaigns) {
    $state.records = campaigns;
  },

  setCurrentCampaign($state, campaign) {
    $state.currentCampaign = campaign;
  },

  addCampaign($state, campaign) {
    $state.records.unshift(campaign);
  },

  replaceCampaign($state, campaign) {
    const index = $state.records.findIndex(record => record.id === campaign.id);
    if (index >= 0) {
      $state.records.splice(index, 1, campaign);
    } else {
      $state.records.unshift(campaign);
    }

    if ($state.currentCampaign?.id === campaign.id) {
      $state.currentCampaign = campaign;
    }
  },

  removeCampaign($state, id) {
    $state.records = $state.records.filter(record => record.id !== id);
    if ($state.currentCampaign?.id === id) {
      $state.currentCampaign = null;
    }
  },

  setRecipients($state, recipients) {
    $state.recipients = recipients;
  },

  setRecipientsMeta($state, meta) {
    $state.recipientsMeta = {
      total: meta.total || 0,
      page: meta.page || 1,
      perPage: meta.per_page || 50,
      totalPages: meta.total_pages || 1,
    };
  },

  setUIFlags($state, payload) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...payload,
    };
  },

  setLastError($state, error) {
    $state.lastError = error;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
